import { createElement } from 'react'
import {
  DEVICES,
  FAMILY,
  FEATURES,
  FEATURE_ORDER,
  INVITEE,
  MANAGER,
  RENEWAL_DATE,
  SEATS,
  TABLE,
  TERM_LABEL,
  eur,
  type Device,
  type Person,
} from './data'
import { MEMBER_BREACH } from './data'
import { ARCHITECTURES } from './architecture'
import { flowPreset } from './flows'
import {
  CONFIG,
  SHARES_OFF,
  SHARES_ON,
  isSharingAnything,
  type DemoState,
  type DeviceState,
  type Flow,
} from './state'
import type { DemoApi } from './useDemo'
import { qrDataUri } from '../lib/qr'

const ACC = 'var(--bg-accent)'

const QR_INVITE = createElement('img', {
  src: qrDataUri(7331),
  alt: 'QR code',
  style: { width: '100%', height: '100%', imageRendering: 'pixelated' },
})
const QR_ALERTS = createElement('img', {
  src: qrDataUri(4207),
  alt: 'QR code',
  style: { width: '100%', height: '100%', imageRendering: 'pixelated' },
})

/**
 * Everything the design template binds to, computed from state.
 *
 * This is the port of the design doc's `renderVals()`. Handlers are built here
 * rather than in the store because most of them close over the current state —
 * exactly as they did in the original.
 */
export function deriveVals(s: DemoState, api: DemoApi) {
  const arch = ARCHITECTURES[s.arch]
  const { set, update, toast } = api

  // ── pricing ──────────────────────────────────────────────────────
  const sel = (on: boolean) => (on ? ACC : 'var(--border-primary)')
  const upRow = TABLE[s.upTerm]

  // ── membership ───────────────────────────────────────────────────
  const nextInvitee: Omit<Person, 'role' | 'active'> & Partial<Person> = !s.inviteTarget
    ? INVITEE
    : !s.invites.find((i) => i.key === 'katherine')
      ? FAMILY[0]
      : FAMILY[1]

  const filled = 1 + (s.inviteTarget ? 1 : 0) + s.invites.length
  const seatBars = Array.from({ length: SEATS }, (_, i) => ({
    color: i < filled ? ACC : 'var(--bg-tertiary)',
  }))

  const pending = {
    statusLabel: 'Invited',
    statusBg: 'var(--bg-warning-subtle)',
    statusColor: 'var(--text-warning)',
  }
  const active = {
    statusLabel: 'Active',
    statusBg: 'var(--bg-success-subtle)',
    statusColor: 'var(--text-success)',
  }
  const share = (on: boolean) =>
    on
      ? { shareLabel: arch.copy.sharingLabel, shareColor: 'var(--text-success)' }
      : { shareLabel: arch.copy.notSharingLabel, shareColor: 'var(--text-secondary)' }

  type MemberRow = {
    key: string
    name: string
    email: string
    role: string
    initials: string
    tint: string
    statusLabel: string
    statusBg: string
    statusColor: string
    shareLabel: string
    shareColor: string
    open: () => void
  }

  /**
   * Group membership is about people. Devices never appear here — under zero
   * knowledge there is no link to draw, and under the mapped model the link
   * belongs on the member's own screen, not in a roster.
   *
   * A member is known by their invite email until the manager gives them an
   * alias, which mirrors how an invite actually starts life.
   */
  const displayName = (key: string, fallback: string) => s.aliases[key] ?? fallback

  /**
   * What a device is called on screen.
   *
   * `fallback` is whatever the model would call it by default — the full name
   * under the mapped model, the owner stripped off it under zero knowledge. A
   * name someone typed replaces that outright and is left exactly as typed.
   */
  const deviceLabel = (real: string, fallback: string) => s.deviceNames[real] ?? fallback

  const members: MemberRow[] = [
    {
      ...MANAGER,
      name: 'You',
      role: 'Group manager',
      tint: ACC,
      ...active,
      shareLabel: 'This device',
      shareColor: 'var(--text-secondary)',
      open: () => {},
    },
  ]
  if (s.inviteTarget) {
    members.push({
      key: 'vito',
      name: displayName('vito', INVITEE.email),
      email: INVITEE.email,
      initials: INVITEE.initials,
      tint: INVITEE.tint,
      role: s.joined ? 'Member' : 'Invite sent',
      ...(s.joined ? active : pending),
      ...share(s.joined && s.deviceAccepted),
      open: () => {},
    })
  }
  s.invites.forEach((i) => {
    members.push({
      key: i.key,
      name: displayName(i.key, i.email),
      email: i.email,
      initials: i.initials,
      tint: i.tint,
      role: i.active ? 'Member' : 'Invite sent',
      ...(i.active ? active : pending),
      ...share(i.active && i.key === 'katherine'),
      open: () => {},
    })
  })
  members.forEach((mem) => {
    // Only the mapped model has anything behind a member: their devices. Under
    // zero knowledge the row is the whole story, so it does not navigate.
    mem.open =
      arch.exposesMemberDevices && mem.key !== 'self'
        ? () => set({ m: 'member', memberKey: mem.key })
        : () => {}
  })

  const selected = members.find((mem) => mem.key === s.memberKey) ?? members[0]

  // ── per-device sharing ───────────────────────────────────────────
  const askVito = () => set({ deviceAsked: true, k: 'lock', kPush: 'device', flow: 3 })

  const setDev = (name: string, val: DeviceState) =>
    update((prev) => ({ devState: { ...prev.devState, [name]: val } }))

  const devStateFor = (dv: Device): DeviceState | 'own' => {
    if (dv.own) return 'own'
    const override = s.devState[dv.name]
    if (dv.vito) {
      if (s.deviceAccepted) return 'sharing'
      if (override === 'off') return 'off'
      return s.deviceAsked ? 'pending' : 'off'
    }
    if (override) return override
    return dv.sharing ? 'sharing' : 'off'
  }

  const ownedDevices = DEVICES[selected.key] ?? []

  /** The member screen only exists under the mapped model, so this is real. */
  const deviceRows: Device[] = ownedDevices

  const memberDevices = deviceRows
    .filter((dv) => s.devState[dv.name] !== 'gone')
    .map((dv) => {
      const st = devStateFor(dv)

      if (st === 'own') {
        return {
          renameKey: dv.name,
          name: deviceLabel(dv.name, dv.name),
          status: 'This device',
          color: 'var(--text-secondary)',
          hasBtn: false,
          btnLabel: '',
          btnBorder: 'transparent',
          btnBg: 'transparent',
          btnColor: 'transparent',
          action: () => {},
        }
      }

      if (st === 'sharing') {
        return {
          renameKey: dv.name,
          name: deviceLabel(dv.name, dv.name),
          status: arch.copy.sharingLabel,
          color: 'var(--text-success)',
          hasBtn: true,
          btnLabel: 'Stop',
          btnBorder: 'var(--border-critical)',
          btnBg: 'transparent',
          btnColor: 'var(--text-critical)',
          action: () => {
            if (dv.vito) set({ deviceAccepted: false, deviceAsked: false, memberShares: SHARES_OFF })
            else setDev(dv.name, 'off')
            toast('m', `${deviceLabel(dv.name, dv.name)} stopped sharing alerts`)
          },
        }
      }

      if (st === 'pending') {
        return {
          renameKey: dv.name,
          name: deviceLabel(dv.name, dv.name),
          status: 'Request sent — waiting',
          color: 'var(--text-warning)',
          hasBtn: true,
          btnLabel: 'Cancel',
          btnBorder: 'var(--border-primary)',
          btnBg: 'transparent',
          btnColor: 'var(--text-primary)',
          action: () => {
            if (dv.vito) set({ deviceAsked: false, kPush: 'none' })
            else setDev(dv.name, 'off')
            toast('m', 'Request cancelled')
          },
        }
      }

      return {
        renameKey: dv.name,
        name: deviceLabel(dv.name, dv.name),
        status: arch.copy.notSharingLabel,
        color: 'var(--text-secondary)',
        hasBtn: true,
        btnLabel: 'Request',
        btnBorder: 'transparent',
        btnBg: ACC,
        btnColor: '#fff',
        action: dv.vito
          ? askVito
          : () => {
              setDev(dv.name, 'pending')
              toast('m', `Request sent to ${deviceLabel(dv.name, dv.name)}`)
            },
      }
    })

  /**
   * Rows on the Group alerts setup screen.
   *
   * The imported design hardcoded two device rows, which only makes sense under
   * the mapped model. Here the list is derived instead: devices when the
   * manager is allowed to enumerate them, otherwise one row per person.
   */
  const activeMembers = members.filter((mem) => mem.key !== 'self' && mem.statusLabel === 'Active')

  const groupShareTargets = arch.exposesMemberDevices
    ? activeMembers.flatMap((mem) =>
        (DEVICES[mem.key] ?? []).map((dv) =>
          shareTarget(deviceLabel(dv.name, dv.name), dv, deviceIcon(dv.name))
        )
      )
    : // Zero knowledge still has a device list — it simply has no owner column.
      // Stripping the possessive is the whole difference: same handsets, no
      // line back to a member.
      activeMembers.flatMap((mem) =>
        (DEVICES[mem.key] ?? []).map((dv) =>
          shareTarget(deviceLabel(dv.name, unownedName(dv.name)), dv, deviceIcon(dv.name))
        )
      )

  function shareTarget(label: string, dv: Device, icon: string) {
    const st = devStateFor(dv)
    const paused = dv.vito ? s.deviceAccepted && s.sharingPaused : st === 'paused'
    const on = (st === 'sharing' && !paused) || (dv.vito && s.deviceAccepted && !paused)
    const waiting = st === 'pending'

    const setPaired = (next: DeviceState) => {
      if (dv.vito) {
        if (next === 'paused') set({ sharingPaused: true })
        else if (next === 'sharing') set({ sharingPaused: false })
        else set({ deviceAccepted: false, deviceAsked: false, sharingPaused: false, memberShares: SHARES_OFF })
      } else {
        setDev(dv.name, next)
      }
    }

    return {
      // Keyed by the handset, not by what it is currently called.
      key: dv.name,
      renameKey: dv.name,
      name: label,
      icon,
      isOn: on,
      isPaused: paused,
      togglePause: () => {
        setPaired(paused ? 'sharing' : 'paused')
        toast('m', paused ? `${label} resumed sharing` : `${label} paused`)
      },
      revoke: () => {
        setPaired('off')
        toast('m', `${label} is no longer paired`)
      },
      canAsk: !on && !waiting,
      statusLabel: on
        ? arch.copy.sharingLabel
        : waiting
          ? 'Request sent — waiting'
          : arch.copy.notSharingLabel,
      statusColor: on
        ? 'var(--text-success)'
        : waiting
          ? 'var(--text-warning)'
          : 'var(--text-secondary)',
      /**
       * Asking is a single tap. The mapped model asks a device it already
       * knows about, and zero knowledge has just handed over a code or a
       * link — neither needs a confirmation screen in between.
       */
      ask: () => {
        if (dv.vito) askVito()
        else setDev(dv.name, 'pending')
        toast('m', `Request sent to ${label}`)
      },
    }
  }

  // ── alert feed ───────────────────────────────────────────────────
  const recent = s.flow >= 4 ? s.alerts.filter((a) => a.time !== '2 days ago').length : 0
  const sharing = (s.deviceAccepted ? 1 : 0) + (s.invites.some((i) => i.active) ? 1 : 0)

  /** Protect-tab toggles on the member handset. */
  const memberFeatureRows = FEATURE_ORDER.map((id) => {
    const on = s.memberFeatures[id]
    return {
      id,
      name: FEATURES[id].name,
      icon: `/assets/icons/${FEATURES[id].icon}.svg`,
      on,
      trackBg: on ? ACC : 'var(--bg-disabled)',
      knobSide: on ? 'right:3px' : 'left:3px',
      toggle: () => api.setMemberFeature(id, !on),
    }
  })

  /** Attribution is the whole architectural question — it lives here. */
  const alertRows =
    s.flow >= 4
      ? s.alerts
          .filter((a) => a.time !== '2 days ago')
          .map((a) => {
            const isFeature = a.kind === 'feature'
            const isBreach = a.kind === 'breach'

            // A protection someone switched off is a gap, not an attack — it
            // reads as a warning, and it is the one alert the manager can act on.
            const tone = isFeature
              ? a.resolved
                ? { fg: 'var(--text-success)', bg: 'var(--bg-success-subtle)', border: 'var(--border-success)' }
                : { fg: 'var(--text-warning)', bg: 'var(--bg-warning-subtle)', border: 'var(--border-warning)' }
              : { fg: 'var(--text-critical)', bg: 'var(--bg-critical-subtle)', border: 'var(--border-critical)' }

            return {
              /**
               * Both models deliver the same alert — zero knowledge relays it
               * device to device rather than aggregating it away. All that
               * changes is the name on it: the handset keeps its own identity,
               * it just stops carrying its owner's.
               */
              title: a.title,
              /**
               * A breach reads differently on this side. The member's own
               * Monitor tab names the service and what leaked; the managing
               * account is told only that a breach happened, so nothing about
               * where the member has accounts travels with the alert.
               */
              detail: isBreach
                ? 'Credentials for this account turned up in a dark web dump.'
                : a.detail,
              source: isBreach
                ? (a.account ?? '')
                : deviceLabel(a.device, arch.attributesAlerts ? a.device : unownedName(a.device)),
              sourceIcon: isBreach
                ? '/assets/icons/account.svg'
                : '/assets/icons/phone-apple.svg',
              time: a.time,
              icon: isFeature
                ? `/assets/icons/${FEATURES[a.feature!].icon}.svg`
                : isBreach
                  ? '/assets/icons/security-score.svg'
                  : '/assets/icons/threat-protection.svg',
              accent: tone.fg,
              iconBg: tone.bg,
              border: tone.border,
              canRestore: isFeature && !a.resolved && arch.supportsRemoteFeatureControl,
              restore: () => (a.feature ? api.restoreFeature(a.feature) : undefined),
              isResolved: Boolean(isFeature && a.resolved),
            }
          })
      : []

  const kAlertRows = s.kAlerts.map((a) => ({
    title: a.threat.title,
    detail: a.threat.detail,
    time: a.threat.time,
    shared: a.shared
      ? arch.attributesAlerts
        ? 'Shared with Michael'
        : 'Shared with paired device'
      : 'Not shared',
  }))

  /**
   * Tooltips describe roles, not the cast. The demo uses named characters so
   * the two handsets are easy to talk about, but the argument is about a
   * managing account and the accounts and devices it supervises — the copy
   * that explains the architecture should say so.
   */
  const flowNotes: Record<Flow, string> = {
    1: 'The managing account already pays for Complete. It buys Group from the subscription: all 5 seats charged upfront, billed separately from the existing plan. Identical under both architectures.',
    2: 'The managing account invites someone; the supervised device gets the invite and joins. Membership only — no alert sharing yet. Watch the row change from Invite sent to Member. Identical under both architectures.',
    3: arch.attributesAlerts
      ? 'Devices hang off the member who owns them, so sharing is arranged from that member and needs no separate pairing. Once on, alerts reach every device the managing account owns.'
      : 'Sharing is a handshake between two devices, arranged apart from membership: any device in the group can pair with any other, and neither side learns whose it is.',
    4: arch.attributesAlerts
      ? "Fire a threat from this card. It reaches the managing account's alerts within seconds, attributed to the supervised device."
      : 'Fire a threat from this card. The paired device receives the same alert — it names the handset, but nothing says which member holds it.',
  }

  const blocked = s.blocked

  const goFlow = (flow: Flow) => set(flowPreset(flow, s.arch))

  return {
    // ── sidebar ────────────────────────────────────────────────────
    flowNotes,
    flow: s.flow,

    // ── stage chrome ───────────────────────────────────────────────
    managerCaption: 'iPhone 15 Pro — the managing side: plan, seats, invites and the alert feed.',
    memberCaption: 'iPhone 15 — the member side. Everything Michael can see is opt-in, controlled here.',
    showMember: s.flow >= 2,
    hasGroup: s.flow >= 2,
    noGroupYet: s.flow < 2,
    subPlanName: s.flow >= 2 ? 'NordVPN Group Plan' : 'Complete',
    subPlanLine:
      s.flow >= 2
        ? `${SEATS} seats · renews ${RENEWAL_DATE[s.upTerm]}`
        : 'Renews on 28 Aug · 1-year plan',
    // The upsell has done its job once the group exists — it only belongs in
    // Flow 1, where there is still something to sell.
    showConnectPromo: s.flow < 2,
    connectPromoSub: `5 accounts from ${eur(TABLE.y2.group)}/mo`,

    // ── manager screens ────────────────────────────────────────────
    mLock: s.m === 'lock',
    mAlertPush: s.m === 'lock' && !!s.mPush,
    mQuietLock: s.m === 'lock' && !s.mPush,
    // A breach names the account, so it reads the same under either model and
    // takes "for", not "on" — nobody's phone did anything here.
    mPushText: !s.mPush
      ? ''
      : s.mPush.kind === 'breach'
        ? `${s.mPush.title} for ${s.mPush.account}`
        : `${s.mPush.title} on ${deviceLabel(s.mPush.device, arch.attributesAlerts ? s.mPush.device : unownedName(s.mPush.device))}`,
    mSub: s.m === 'sub',
    mTerm: s.m === 'term',
    mCheckout: s.m === 'checkout',
    mSuccess: s.m === 'success',
    mInvite: s.m === 'invite',
    mGroupAlerts: s.m === 'alertsetup',
    mAlerts: s.m === 'alerts',
    mConnect: s.m === 'connect',
    mProtect: s.m === 'protect',
    mMonitor: s.m === 'monitor',
    mProfile: s.m === 'profile',
    mMember: s.m === 'member',
    mAppChrome: ['connect', 'protect', 'monitor', 'sub', 'alertsetup'].includes(s.m),
    managerThreats: String(recent),

    // ── member screens ─────────────────────────────────────────────
    kLock: s.k === 'lock',
    kJoin: s.k === 'join',
    kMail: s.k === 'mail',
    kMessages: s.k === 'messages',
    kSignup: s.k === 'signup',
    memberEmailAddress: INVITEE.email,
    managerEmailAddress: MANAGER.email,
    kHome: s.k === 'home',
    kProtect: s.k === 'protect',
    kMonitor: s.k === 'monitor',
    kProfile: s.k === 'profile',
    kAlerts: s.k === 'alerts',
    kAppChrome: ['home', 'protect', 'monitor', 'profile'].includes(s.k),
    kAlertRows: kAlertRows,
    kNoAlerts: kAlertRows.length === 0,
    kUnread: s.kUnread,
    kHasUnread: s.kUnread > 0,
    kDeviceRequest: s.k === 'devreq',
    kDeviceOff: s.k === 'devoff',
    kBlocked: s.k === 'blocked',
    kBreachPush: s.k === 'lock' && s.kPush === 'breach',
    kMailPush: s.k === 'lock' && s.kPush === 'mail',
    kMessagePush: s.k === 'lock' && s.kPush === 'message',
    kDevicePush: s.k === 'lock' && s.kPush === 'device',
    kQuietLock: s.k === 'lock' && s.kPush === 'none',
    kBlockCount: s.kBlockCount,

    // ── member profile ─────────────────────────────────────────────
    memberPlanName: 'NordVPN Group Plan',
    memberPlanLine: `Your seat · paid by ${MANAGER.email}`,
    memberGroupBody: `Your plan is paid by ${MANAGER.email}. Leaving the group keeps your account — you would just pay for it yourself.`,

    /**
     * What the member can opt into.
     *
     * Under the mapped model these are per-feature consents, because the group
     * can address this device. Zero knowledge has no device to address, so the
     * same intent is expressed as two device-to-device exchanges instead —
     * which is the architecture's name made literal.
     */
    /**
     * Sharing is Flow 3's subject. Through the purchase and the invite the
     * group is still only seats and billing, so the member's profile has
     * nothing to offer here — and controls that do not matter yet read as if
     * something has already been arranged.
     */
    memberShowGroupFeatures: s.flow >= 3,

    /**
     * Zero knowledge shows the member the device it is paired with. The mapped
     * model has no pair to name — the device belongs to a member, and the group
     * is what makes it addressable — so the honest equivalent is saying plainly
     * who is on the other end of that arrangement.
     */
    memberSupervisedBy:
      arch.exposesMemberDevices && isSharingAnything(s.memberShares)
        ? {
            title: `Supervised by ${MANAGER.email}`,
            body: 'This device tells the group manager about the threats it blocks. What it sends is the list below — switch any of it off and it stops.',
          }
        : null,

    memberSharesFeatures: arch.exposesMemberDevices,
    memberShareRows: (
      [
        ['web', 'Share web threats alerts'],
        ['call', 'Share scam call alerts'],
        ['remote', 'Remote feature control'],
        ['monitor', 'Share monitoring alerts'],
      ] as const
    ).map(([id, name]) => ({
      id,
      name,
      on: s.memberShares[id],
      trackBg: s.memberShares[id] ? ACC : 'var(--bg-disabled)',
      knobSide: s.memberShares[id] ? 'right:3px' : 'left:3px',
      /**
       * A self-reported opt-in. No handshake to run and no reason to leave the
       * screen — the member flips it, and the managing account is simply told.
       * That directness is what the device-to-user mapping buys.
       */
      toggle: () => {
        const next = !s.memberShares[id]
        update((prev) => {
          const shares = { ...prev.memberShares, [id]: next }
          // Sharing nothing is the same fact as not sharing, so the manager's
          // per-device status follows the toggles rather than drifting from them.
          const sharing = isSharingAnything(shares)
          return {
            memberShares: shares,
            deviceAccepted: sharing,
            // Switching everything off is a revocation, not a pending request —
            // clearing `deviceAsked` is what stops it reading as "waiting".
            deviceAsked: sharing,
            sharingPaused: false,
          }
        })
        toast('m', `${INVITEE.email} ${next ? 'enabled' : 'disabled'} ${name}`)
      },
    })),
    memberBreaches: s.breaches.map((b) => ({
      title: b.title,
      service: b.subject,
      leaked: MEMBER_BREACH.leaked,
      when: MEMBER_BREACH.when,
      time: b.time,
    })),
    memberHasBreach: s.breaches.length > 0,
    memberBreachCount: String(s.breaches.length),

    // ── upgrade screens ────────────────────────────────────────────
    groupFromEurSuffix: `${TABLE.y2.group.toFixed(2)}€`,
    groupFullMonthly: eur(TABLE.m.group),
    groupMonthly: eur(upRow.group),
    upTermLabel: TERM_LABEL[s.upTerm],
    upMPrice: eur(TABLE.m.group),
    upYPrice: eur(TABLE.y.group),
    up2Price: eur(TABLE.y2.group),
    upMTotal: `${eur(TABLE.m.group)} billed monthly`,
    upYTotal: `${eur(TABLE.y.group * 12)} billed upfront`,
    up2Total: `${eur(TABLE.y2.group * 24)} billed upfront`,
    upMSave: 'per month',
    upYSave: 'save 54%',
    up2Save: 'save 58%',
    upMBorder: sel(s.upTerm === 'm'),
    upMBg: s.upTerm === 'm' ? 'var(--bg-accent-subtle)' : 'var(--bg-secondary)',
    upYBorder: sel(s.upTerm === 'y'),
    upYBg: s.upTerm === 'y' ? 'var(--bg-accent-subtle)' : 'var(--bg-secondary)',
    up2Border: sel(s.upTerm === 'y2'),
    up2Bg: s.upTerm === 'y2' ? 'var(--bg-accent-subtle)' : 'var(--bg-secondary)',
    upfrontLabel: s.upTerm === 'm' ? 'Billed monthly' : 'Billed upfront',
    upfrontTotal: eur(upRow.group * upRow.months),
    nextBillDate: RENEWAL_DATE[s.upTerm],
    successBody: `Group plan with 5 seats. Renewal date ${RENEWAL_DATE[s.upTerm]}`,

    // ── group / seats ──────────────────────────────────────────────
    // ── group management ───────────────────────────────────────────
    groupName: s.groupName,
    groupNameDraft: s.groupNameDraft ?? '',
    isEditingGroupName: s.groupNameDraft !== null,
    startEditGroupName: () => set({ groupNameDraft: s.groupName }),
    changeGroupNameDraft: (value: string) => set({ groupNameDraft: value }),
    saveGroupName: () =>
      set({ groupName: (s.groupNameDraft ?? '').trim() || s.groupName, groupNameDraft: null }),
    cancelEditGroupName: () => set({ groupNameDraft: null }),

    seatsUsed: filled,
    seatsTotal: SEATS,
    seatsFree: SEATS - filled,
    seatsLeftLabel: `${SEATS - filled} free`,
    // The invite affordance disappears rather than failing when the group is full.
    canInviteMore: filled < SEATS,
    membersHaveDepth: arch.exposesMemberDevices,

    aliasEditingKey: s.aliasDraft?.key ?? null,
    aliasDraft: s.aliasDraft?.value ?? '',
    startAlias: (key: string, current: string) =>
      set({ aliasDraft: { key, value: s.aliases[key] ?? current } }),
    changeAliasDraft: (value: string) =>
      update((prev) => ({
        aliasDraft: prev.aliasDraft ? { ...prev.aliasDraft, value } : null,
      })),
    saveAlias: () =>
      update((prev) => {
        if (!prev.aliasDraft) return {}
        const name = prev.aliasDraft.value.trim()
        const next = { ...prev.aliases }
        // Clearing the field puts the member back to their invite email.
        if (name) next[prev.aliasDraft.key] = name
        else delete next[prev.aliasDraft.key]
        return { aliases: next, aliasDraft: null }
      }),
    cancelAlias: () => set({ aliasDraft: null }),

    /**
     * Renaming a device, on whichever side is looking at it. A handset arrives
     * named by whoever set it up ("Vito's iPhone"); the person reading the list
     * is free to call it something they recognise. Only the label moves — the
     * pairing, the alerts and the state behind it stay keyed by the handset.
     */
    deviceNameEditingKey: s.deviceNameDraft?.key ?? null,
    deviceNameDraft: s.deviceNameDraft?.value ?? '',
    startDeviceName: (key: string, current: string) =>
      set({ deviceNameDraft: { key, value: s.deviceNames[key] ?? current } }),
    changeDeviceNameDraft: (value: string) =>
      update((prev) => ({
        deviceNameDraft: prev.deviceNameDraft ? { ...prev.deviceNameDraft, value } : null,
      })),
    saveDeviceName: () =>
      update((prev) => {
        if (!prev.deviceNameDraft) return {}
        const name = prev.deviceNameDraft.value.trim()
        const next = { ...prev.deviceNames }
        // Clearing the field puts the handset back to the name it came with.
        if (name) next[prev.deviceNameDraft.key] = name
        else delete next[prev.deviceNameDraft.key]
        return { deviceNames: next, deviceNameDraft: null }
      }),
    cancelDeviceName: () => set({ deviceNameDraft: null }),
    seatBars,
    members,
    profileGroupSub: `${filled} of ${SEATS} seats used`,

    memberName: selected.name,
    memberEmail: selected.email ?? '',
    memberRole: selected.role,
    memberInitials: selected.initials,
    memberTint: selected.tint,
    /** Shown under the device list, in place of the member-level ask. */
    memberDevicesNote: arch.copy.requestBody,
    memberDevices,
    memberFeatureRows,
    groupShareTargets,
    groupShareHeading: arch.copy.groupShareHeading,
    groupShareIntro: arch.copy.groupShareIntro,
    groupSharePrivacyNote: arch.copy.groupSharePrivacyNote,

    /**
     * Who the emailed pairing request goes to. Zero knowledge has no device
     * list to pick from — the code, the link and the email are the handshake —
     * so this names the account whose handset the demo drives, never a device.
     */
    pairInviteName: INVITEE.email,
    pairInviteSub: 'Whoever holds the device decides, on the device',
    pairInviteInitials: INVITEE.initials,
    pairInviteTint: INVITEE.tint,

    // ── alerts ─────────────────────────────────────────────────────
    alertRows,
    noAlertsYet: alertRows.length === 0,
    /**
     * Pairing two devices is a zero-knowledge idea. The mapped model has no
     * handshake to offer: a device belongs to a member, so sharing is arranged
     * from that member's screen and reaches every device the manager owns.
     *
     * Either way there is nothing to arrange until someone else is in the
     * group, so the whole section waits for a second seat to be filled.
     */
    showGroupAlertsCard: !arch.exposesMemberDevices && filled > 1,

    /**
     * Group-wide statistics, which only the mapped model can produce: they are
     * built from per-account history, and zero knowledge keeps none. The
     * clearest thing one architecture can do that the other simply cannot.
     * Also held back until the group has someone to report on.
     */
    showGroupStats: arch.exposesMemberDevices && filled > 1,
    groupStats: {
      posture: recent > 2 ? 'Needs attention' : 'Good',
      postureColor: recent > 2 ? 'var(--text-warning)' : 'var(--text-success)',
      postureBg: recent > 2 ? 'var(--bg-warning-subtle)' : 'var(--bg-success-subtle)',
      tiles: [
        { key: 'trackers', value: '1,284', label: 'trackers blocked' },
        { key: 'threats', value: String(4 + recent), label: 'threats blocked' },
        { key: 'breaches', value: String(s.breaches.length), label: 'breaches found' },
      ],
      // Seven days of blocked threats; today grows as the demo fires triggers.
      week: [3, 6, 2, 8, 4, 5, 2 + recent].map((n, i, all) => ({
        key: i,
        heightPct: Math.round((n / Math.max(...all)) * 100),
        today: i === all.length - 1,
      })),
    },

    /**
     * Pairs that have completed a handshake. A pair can be muted without being
     * torn down — pausing keeps the pairing, revoking ends it — which is the
     * distinction a device-to-device model needs and a one-way toggle loses.
     */
    pairedDevices: groupShareTargets
      .filter((t) => t.isOn || t.isPaused)
      .map((t) => ({
        key: t.key,
        renameKey: t.renameKey,
        name: t.name,
        icon: t.icon,
        paused: t.isPaused,
        statusLabel: t.isPaused ? 'Paused' : arch.copy.sharingLabel,
        statusColor: t.isPaused ? 'var(--text-warning)' : 'var(--text-success)',
        togglePause: t.togglePause,
        revoke: t.revoke,
      })),

    /**
     * The same pairing, seen from the supervised handset.
     *
     * Zero knowledge is symmetric — neither side is the authority — so the
     * device sending the alerts lists the one receiving them, and can pause or
     * tear the pair down from here exactly as the other side can. The name is
     * stripped of its owner in both directions: this device is told which
     * handset it is paired with, never whose it is.
     */
    memberPairedDevices:
      !arch.exposesMemberDevices && s.deviceAccepted
        ? [
            {
              key: 'manager',
              renameKey: MANAGER_DEVICE,
              name: deviceLabel(MANAGER_DEVICE, unownedName(MANAGER_DEVICE)),
              icon: deviceIcon(MANAGER_DEVICE),
              paused: s.sharingPaused,
              statusLabel: s.sharingPaused ? 'Paused' : 'Receiving your alerts',
              statusColor: s.sharingPaused ? 'var(--text-warning)' : 'var(--text-success)',
              togglePause: () => {
                const next = !s.sharingPaused
                set({ sharingPaused: next })
                toast(
                  'k',
                  next
                    ? 'Paused. Nothing leaves this device until you resume.'
                    : 'Resumed. Blocked threats reach the paired device again.'
                )
                toast('m', `${OWN_DEVICE} ${next ? 'paused' : 'resumed sharing'}`)
              },
              revoke: () => {
                set({
                  deviceAccepted: false,
                  deviceAsked: false,
                  sharingPaused: false,
                  memberShares: SHARES_OFF,
                })
                toast('k', 'Pairing ended. Only you see what gets blocked.')
                toast('m', `${OWN_DEVICE} is no longer paired`)
              },
            },
          ]
        : [],

    // ── zero-knowledge pairing ─────────────────────────────────────
    mShareInvite: s.m === 'shareinvite',
    mScanner: s.m === 'scanner',
    kShareInvite: s.k === 'shareinvite',
    kScanner: s.k === 'scanner',

    /**
     * The pairing entry points. Byte-identical on both handsets on purpose —
     * in a device-to-device model neither side is the authority, so neither
     * gets a different screen.
     */
    shareCards: [
      {
        key: 'invite',
        title: 'Invite a device to share alerts',
        body: 'Send a request to a device in the group, or show a code for it to scan. You receive the threats it blocks.',
      },
      {
        key: 'join',
        title: "Join a device's alert sharing",
        body: 'Scan the code another device is showing to start sending it the threats you block.',
      },
    ],
    managerOpenShareInvite: () => set({ m: 'shareinvite' }),
    managerOpenScanner: () => set({ m: 'scanner' }),
    managerClosePairing: () => set({ m: 'sub' }),
    memberOpenShareInvite: () => set({ k: 'shareinvite' }),
    memberOpenScanner: () => set({ k: 'scanner' }),
    memberClosePairing: () => set({ k: 'profile' }),

    /**
     * The invite screen no longer lists devices, so there is nothing to pick:
     * the request goes to the first device in the group that could accept one.
     */
    sendPairRequest: () => {
      const target = groupShareTargets.find((t) => t.canAsk) ?? groupShareTargets[0]
      set({ m: 'sub', k: 'profile' })
      if (target) target.ask()
      else toast('m', 'No devices in the group yet')
    },

    /** A scan stands in for the handshake completing. */
    completePairing: (side: 'm' | 'k') => {
      set({
        deviceAccepted: true,
        deviceAsked: true,
        memberShares: SHARES_ON,
        m: side === 'm' ? 'sub' : s.m,
        k: 'profile',
      })
      toast('m', 'Paired — this device now sends the threats it blocks')
      toast('k', 'Paired. Blocked threats only, not linked to your account.')
    },
    alertsCardTitle:
      sharing || filled > 1 ? 'Group alerts' : 'Invite group members to enable group features',
    alertsCardSub:
      filled > 1
        ? `${recent ? `${recent} blocked in the last hour` : 'Nothing blocked today'} · ${sharingCount(sharing)}`
        : 'Once someone joins, ask their device to share the threats it blocks.',
    alertsCardBorder: 'var(--border-secondary)',
    alertsCardBg: filled > 1 ? 'var(--bg-product-meshnet)' : 'var(--bg-tertiary)',
    alertsCardCursor: filled > 1 ? 'pointer' : 'default',
    alertsCardOpacity: filled > 1 ? '1' : '0.55',
    unread: s.unread,
    hasUnread: s.unread > 0,
    hasAlerts: s.flow >= 4 && s.alerts.length > 0,

    blockedIsCall: blocked?.kind === 'call',
    blockedIsSite: blocked?.kind === 'site',
    blockedTitle: blocked?.kind === 'site' ? 'Site blocked' : 'Call blocked',
    blockedBody:
      blocked?.kind === 'site'
        ? 'That page was set up to steal logins. We stopped it before it loaded.'
        : "This number is being reported for delivery-fee scams. We didn't let it ring.",
    blockedSubject: blocked?.subject ?? '',
    blockedSecondary: blocked?.kind === 'site' ? 'Open it anyway' : 'Allow this number',
    /** What this device just told the other side, in its own words. */
    blockedShareNote: !s.deviceAccepted
      ? 'Not shared — nobody else was told'
      : s.sharingPaused
        ? 'Sharing paused — nobody else was told'
        : arch.attributesAlerts
          ? `Shared with ${MANAGER.email} — this alert only`
          : 'Sent to the paired device — not linked to you',

    // ── invite ─────────────────────────────────────────────────────
    inviteEmail: s.picked ? nextInvitee.email : 'Email or phone number',
    inviteName: s.lastInvited,
    invitePicked: s.picked,
    inviteFieldBorder: s.picked ? 'var(--border-accent)' : 'var(--border-input)',
    inviteFieldColor: s.picked ? 'var(--text-primary)' : 'var(--text-placeholder)',

    // ── device sharing state ───────────────────────────────────────
    deviceAccepted: s.deviceAccepted,
    shareStateLabel: s.deviceAccepted ? `On · ${arch.copy.consentVisibility}` : 'Off',
    shareStateColor: s.deviceAccepted ? 'var(--text-success)' : 'var(--text-secondary)',
    shareToggleBg: s.deviceAccepted ? ACC : 'var(--bg-disabled)',
    shareKnobLeft: s.deviceAccepted ? '21px' : '3px',

    qrInviteImg: QR_INVITE,
    qrAlertsImg: QR_ALERTS,
    toastM: s.toastM,
    toastK: s.toastK,

    // ── handlers ───────────────────────────────────────────────────
    reset: api.reset,
    goFlow1: () => goFlow(1),
    goFlow2: () => goFlow(2),
    goFlow3: () => goFlow(3),
    goFlow4: () => goFlow(4),

    pickUpM: () => set({ upTerm: 'm' }),
    pickUpY: () => set({ upTerm: 'y' }),
    pickUp2Y: () => set({ upTerm: 'y2' }),

    openTerm: () => set({ m: 'term' }),
    openGroupUpsell: () =>
      set({ m: s.flow === 1 ? 'sub' : 'invite', flow: s.flow === 1 ? 1 : 2, picked: false }),
    openCheckout: () => set({ m: 'checkout' }),
    backToSub: () => set({ m: 'sub' }),
    mOpenSub: () => set({ m: 'sub' }),
    pay: () => set({ m: 'success' }),
    // Both exits from the purchase-success screen land at the start of a flow,
    // so they go through the same presets as the sidebar buttons — otherwise
    // stale member-handset state (an invite push, say) survives the jump.
    skipSetup: () => goFlow(1),
    toDash: () => goFlow(2),

    openInvite: () => set({ m: 'invite', flow: 2, picked: false }),
    shareSheetOpen: s.shareSheet,
    openShareSheet: () => set({ shareSheet: true, shareSheetKind: 'invite' }),
    openAlertShareSheet: () => set({ shareSheet: true, shareSheetKind: 'alerts' }),
    shareSheetTitle:
      s.shareSheetKind === 'alerts'
        ? 'Share alerts with my group'
        : `You're invited to ${s.groupName}`,
    closeShareSheet: () => set({ shareSheet: false }),
    // Only the invitee's contact does anything — the rest are set dressing so
    // the sheet reads like a real one.
    shareContacts: [
      { key: INVITEE.key, initials: INVITEE.initials, tint: INVITEE.tint, name: INVITEE.email, live: true },
      ...FAMILY.map((p) => ({ key: p.key, initials: p.initials, tint: p.tint, name: p.email, live: false })),
    ],
    pickInvitee: () => set({ picked: true }),
    sendInvite: () => {
      if (!s.picked) return
      const who = nextInvitee.email
      // The invite lands as mail on the member's handset; the manager just gets
      // a toast and stays where they are, which is what actually happens.
      if (!s.inviteTarget) {
        set({ inviteTarget: true, picked: false, lastInvited: who, m: 'sub', k: 'lock', kPush: 'mail', flow: 2 })
      } else {
        update((prev) => ({
          invites: [...prev.invites, { ...(nextInvitee as Person), active: false }],
          picked: false,
          lastInvited: who,
          m: 'sub',
          flow: 2,
        }))
      }
      toast('m', `Invite sent to ${who}`)
    },

    /** Sharing the link to a contact delivers it as a message instead. */
    shareInviteTo: (email: string) => {
      if (s.shareSheetKind === 'alerts') {
        // Zero knowledge has no member screen to land on — the ask belongs to
        // the group, so it returns to the group screen instead.
        set(
          arch.exposesMemberDevices
            ? { shareSheet: false, m: 'member', memberKey: 'vito' }
            : { shareSheet: false, m: 'sub' }
        )
        askVito()
        toast('m', `Request sent to ${email}`)
        return
      }
      set({
        shareSheet: false,
        inviteTarget: true,
        picked: false,
        lastInvited: email,
        m: 'sub',
        k: 'lock',
        kPush: 'message',
        flow: 2,
      })
    },

    // The member's route in: notification -> the app it came from -> Nord
    // Account -> the NordVPN app. Each step is somewhere they already are.
    kOpenMail: () => set({ k: 'mail' }),
    kOpenMessages: () => set({ k: 'messages' }),
    kOpenSignup: () => set({ k: 'signup' }),
    kCreateAccount: () => set({ k: 'join' }),
    kBackToLock: () => set({ k: 'lock' }),
    openInviteOnMember: () => set({ k: 'join' }),
    acceptInvite: () => {
      set({ joined: true, inviteTarget: true, k: 'profile', m: 'sub' })
      toast('m', `${INVITEE.email} joined your group`)
      toast('k', 'You\u2019re in. Your seat is paid for.')
    },
    declineInvite: () => {
      set({ k: 'lock', kPush: 'none' })
      toast('m', `${INVITEE.email} hasn\u2019t accepted yet`)
    },

    openDeviceRequest: () => set({ k: 'devreq' }),
    acceptDevice: () => {
      set({ deviceAccepted: true, deviceAsked: true, memberShares: SHARES_ON, k: 'profile' })
      toast(
        'm',
        arch.attributesAlerts
          ? `${CONFIG.memberDevice} is sharing alerts`
          : `A paired device is now sharing alerts`
      )
      toast('k', `Alerts on. ${arch.copy.consentVisibility}.`)
    },
    declineDevice: () => {
      set({ deviceAccepted: false, memberShares: SHARES_OFF, k: 'home' })
      toast('m', 'That device stayed private')
    },
    toggleShare: () => set({ k: s.deviceAccepted ? 'devoff' : 'devreq' }),
    confirmStopSharing: () => {
      set({ deviceAccepted: false, memberShares: SHARES_OFF, k: 'home' })
      toast('m', 'Alert sharing switched off on that device')
      toast('k', 'Sharing off. Only you see what gets blocked.')
    },
    keepSharing: () => set({ k: 'home' }),

    groupAlertsAction: filled > 1 ? () => set({ m: 'alertsetup' }) : () => {},

    blockCall: () => api.block('call'),
    blockSite: () => api.block('site'),

    // Fourth trigger: Dark Web Monitor matches the member's account against a
    // dump. The member's own phone raises it either way; only the mapped model
    // has a channel that can carry it to the managing account.
    raiseBreach: () => api.raiseBreach(INVITEE.name),
    canRaiseBreach: arch.sharesAccountAlerts,
    raiseBreachHint: !arch.sharesAccountAlerts
      ? 'Pairing joins devices, never accounts, so zero knowledge has no channel for an account-level alert. The breach stays on the supervised device.'
      : s.joined
        ? "Dark Web Monitor finds the supervised account's credentials in a dump. It shows on that device's lock screen and Monitor tab, and the managing account is told which member it belongs to."
        : "Dark Web Monitor finds the supervised account's credentials in a dump. It shows on that device's lock screen and Monitor tab; nothing reaches the managing account until the member has joined the group.",

    // Third trigger: the supervised device switches a protection off.
    disableFeature: () => api.setMemberFeature('scamCall', false),
    canDisableFeature: arch.supportsRemoteFeatureControl && s.memberFeatures.scamCall,
    disableFeatureHint: !arch.supportsRemoteFeatureControl
      ? 'Zero knowledge cannot attribute a settings change to a device, so the managing account is never told — and has nothing to switch back on.'
      : s.memberFeatures.scamCall
        ? 'The supervised device switches Scam call protection off on its Protect tab. The managing account is told which device, and can turn it back on from the alert.'
        : "Scam call protection is already off — restore it from the managing account's alert, or from the supervised device's Protect tab.",
    dismissBlocked: () => set({ k: 'home' }),

    mTabConnect: () => set({ m: 'connect' }),
    mTabProtect: () => set({ m: 'protect' }),
    mTabMonitor: () => set({ m: 'monitor' }),
    mProfileOpen: () => set({ m: 'sub' }),
    openAlertsFromLock: () => set({ m: 'alerts', unread: 0, mPush: null }),
    tabAlerts: () => set({ m: 'alerts', unread: 0 }),
    tabGroup: () => set({ m: 'sub' }),
    backFromPush: () => set({ m: 'connect' }),

    kTabConnect: () => set({ k: 'home' }),
    kTabProtect: () => set({ k: 'protect' }),
    kTabMonitor: () => set({ k: 'monitor' }),
    kProfileOpen: () => set({ k: 'profile' }),
    kOpenAlerts: () => set({ k: 'alerts', kUnread: 0 }),

    removeMember: () => {
      const key = s.memberKey
      if (key === 'vito') {
        set({ m: 'sub', inviteTarget: false, joined: false, deviceAccepted: false, deviceAsked: false, memberShares: SHARES_OFF })
      } else if (key !== 'self') {
        update((prev) => ({ m: 'sub', invites: prev.invites.filter((x) => x.key !== key) }))
      } else {
        set({ m: 'sub' })
      }
      if (key !== 'self') toast('m', `${selected.name} removed from your group`)
    },
  }
}

/**
 * Prose, not a status badge — kept separate from `copy.sharingLabel` so the
 * badge can be renamed without turning this into a grammatical accident.
 */
const sharingCount = (n: number) =>
  n ? `${n} ${n === 1 ? 'device' : 'devices'} sharing alerts` : 'no devices sharing alerts yet'


/** Best-guess glyph for a device name, matching the design's icon set. */
function deviceIcon(name: string): string {
  if (/macbook|laptop|air|pro\b/i.test(name)) return '/assets/icons/laptop-apple.svg'
  if (/tv/i.test(name)) return '/assets/icons/tv.svg'
  return '/assets/icons/phone-apple.svg'
}

/** "Katherine's MacBook Air" -> "MacBook Air". */
/** The managing handset, as the supervised device sees it. */
const MANAGER_DEVICE = (DEVICES[MANAGER.key] ?? [])[0]?.name ?? 'iPhone'
/** This handset, as the managing side sees it under zero knowledge. */
const OWN_DEVICE = unownedName(CONFIG.memberDevice)

function unownedName(name: string): string {
  return name.replace(/^[^']+'s\s+/, '')
}
